import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {
  ExportDataCatalogDatasetVariableName,
  SendToDocumentVariableName,
} from 'analysis-tool/AnalysisStepConfigVariableNames';
import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, isAnalysisGraphQL, isAnalysisOutputStepType} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import EntryAndWorksheetSearchInput, {
  DefaultEntryOrWorksheetInfo,
  getDefaultSelectedEntryOrWorksheetId,
} from 'analysis-tool/outputs/EntryAndWorksheetSearchInput';
import OutputsList from 'analysis-tool/outputs/OutputsList';
import {
  isSendToDocumentEnabled,
  isSendToEntryEnabled,
  isSendToWorksheetEnabled,
} from 'analysis-tool/outputs/OutputsUtil';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import FolderSelector from 'file-browser/FolderSelector';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  APPLY_ANALYSIS_TEMPLATE_STARTED_EVENT,
  APPLY_ANALYSIS_TEMPLATE_FINISHED_EVENT,
  RUN_ANALYSIS_TEMPLATE_STARTED_EVENT,
} from 'performance/processAnalysesEvents';
import {PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getDefaultClientIdForView, getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {isValueValid} from 'pipelines/StepShapeUtils';
import {
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import * as AnalysisTemplateActions from './AnalysisTemplateActions';
import {TemplateVariableBinding} from './AnalysisTemplateDAO';
import AnalysisTemplateSelector from './AnalysisTemplateSelector';
import AnalysisTemplateTableSelectorModal, {
  AnalysisTemplateTableSelectorModalProps,
} from './AnalysisTemplateTableSelectorModal';
import {InputTableApplyState, InputTableType, getInputTableApplyStateName} from './AnalysisTemplateUtil';
import ApplyAnalysisTemplatePreview from './ApplyAnalysisTemplatePreview';
import useAnalysisTemplateSelectorState, {TemplateApplyState} from './useAnalysisTemplateSelectorState';
import AnalysisTemplateVariablesForm from './variables/AnalysisTemplateVariablesForm';

interface OutputVariableContext {
  selectedFolderId: string | null;
  selectedDocumentId: string | null;
}

//  Configuration for output-related config variables that are handled by dedicated UI components.
const OUTPUT_RELATED_VARIABLE_CONFIG: Record<
  string,
  {
    getValue: (context: OutputVariableContext) => string | null;
    excludeFromSubmission?: boolean;
  }
> = {
  [ExportDataCatalogDatasetVariableName.FOLDER_ID]: {
    getValue: ({selectedFolderId}) => selectedFolderId,
  },
  [ExportDataCatalogDatasetVariableName.DATASET_NAME]: {
    getValue: () => null,
    excludeFromSubmission: true, // Legacy variable - no longer used
  },
  [SendToDocumentVariableName.DOCUMENT_ID]: {
    getValue: ({selectedDocumentId}) => selectedDocumentId,
  },
  [SendToDocumentVariableName.SECTION_INDEX]: {
    getValue: () => null,
  },
};

// Derived from the config - used to filter variables from the spec editor
const OUTPUT_RELATED_CONFIG_VARIABLE_KEYS = new Set(Object.keys(OUTPUT_RELATED_VARIABLE_CONFIG));

export function InputTableCard({
  analysis,
  index,
  inputTable,
  templateSteps,
  onChange,
  getInputTableDisabledMessage,
}: {
  analysis: AnalysisGraphQL;
  index: number;
  inputTable: InputTableApplyState;
  templateSteps: ReadonlyArray<PipelineStep>;
  onChange: (result: {table: PipelineViewGraphQL; columnMapping: {[templateColumn: string]: string}}) => void;
  getInputTableDisabledMessage?: (
    analysis: AnalysisGraphQL,
    inputTable: PipelineViewGraphQL,
    selectedTable: PipelineViewGraphQL | null
  ) => string | null;
}): JSX.Element {
  const isStatic = inputTable.inputType === InputTableType.STATIC;
  const name = getInputTableApplyStateName(inputTable, templateSteps);
  const description = isStatic
    ? 'This table provides static data and will be added to your analysis'
    : inputTable.description;

  const onOpenModal = (): void => {
    legacyRunModal<AnalysisTemplateTableSelectorModalProps>(AnalysisTemplateTableSelectorModal, {
      analysis,
      inputTable,
      templateSteps,
      onRequestSubmit: onChange,
      getInputTableDisabledMessage,
    });
  };

  return (
    <div className={classNames(css(cardStyles.wrapper), isStatic && css(cardStyles.staticWrapper))}>
      <FieldLabel
        className={classNames(css(cardStyles.header), isStatic && css(cardStyles.staticHeader))}
        label={
          <>
            <BenchlingIcon icon="table" />
            {name}
            {isStatic && <span className={css(cardStyles.staticLabel)}>Static</span>}
          </>
        }
      />
      <div className={css(cardStyles.description)}>{description}</div>
      <div>
        {isStatic ? null : inputTable.table == null ? (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            fullWidth={true}
            onClick={onOpenModal}
            text="Select a table to use"
            dataTestAttrs={{
              component: 'InputTableCard',
              element: 'selectTableButton',
              index: String(index),
              key: name,
            }}
          />
        ) : (
          <div className={css(cardStyles.body)}>
            {name}
            <a onClick={onOpenModal}>
              <BenchlingIcon icon="edit" className={css(cardStyles.editIcon)} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const cardStyles = StyleSheet.create({
  wrapper: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: 4,
    padding: 8,
    marginBottom: 4,
    ':hover': {
      borderColor: Colors.GRAY,
      boxShadow: `0px 2px 6px 1px ${Colors.SILVER}`,
    },
  },
  staticWrapper: {
    background: Colors.GRAY_LIGHTER,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    wordBreak: 'break-word',
  },
  staticHeader: {color: Colors.GRAY_DARKER},
  staticLabel: {
    fontStyle: 'italic',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  description: {color: Colors.GRAY_SEMIDARK, marginBottom: 8},
  body: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  editIcon: {color: Colors.GRAY_DARK},
});

function getDocumentSelectorMessage(): string {
  if (isSendToEntryEnabled() && isSendToWorksheetEnabled()) {
    return 'Select an entry or worksheet to insert outputs into';
  } else if (isSendToEntryEnabled()) {
    return 'Select an entry to insert outputs into';
  }
  return 'Select a worksheet to insert outputs into';
}

export default function AnalysisTemplateApplyView({
  analysis,
  onCloseStepInitializationView,
}: MainViewComponentProps<AnalysisMainViewItemType.TEMPLATE_APPLY>): JSX.Element {
  assert(isAnalysisGraphQL(analysis), 'AnalysisTemplateApplyView only supports analyses.');
  const [templateApplyState, setTemplateApplyState, setSelectedTemplate, setSelectedTemplateId] =
    useAnalysisTemplateSelectorState(analysis.id);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    getDefaultSelectedEntryOrWorksheetId(analysis)
  );
  const [userVariableBindings, setUserVariableBindings] = useState<Record<string, JSONValue>>({});

  const outputSteps = templateApplyState.selectedTemplate
    ? templateApplyState.selectedTemplate.execution.steps.filter((step) =>
        isAnalysisOutputStepType(step.definition.stepType)
      )
    : [];
  const exportDatasetSteps = outputSteps.filter(
    (step) => step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET
  );
  const sendToDocumentSteps = outputSteps.filter(
    (step) => step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT
  );

  const userConfigurableVariables = templateApplyState.selectedTemplate
    ? templateApplyState.selectedTemplate.configVariables.filter(
        (variable) => !OUTPUT_RELATED_CONFIG_VARIABLE_KEYS.has(variable.configVariableKey)
      )
    : [];

  const userVariableBindingsAreComplete = userConfigurableVariables.every((variable) =>
    isValueValid(userVariableBindings[variable.id] ?? null, variable.variableTypeConfigSpec, {})
  );

  const showFolderSelector = exportDatasetSteps.length > 0;
  const showDocumentInput = sendToDocumentSteps.length > 0;
  const hasDocumentOutputsButDisabled = !isSendToDocumentEnabled() && sendToDocumentSteps.length > 0;
  const hasOutputs = outputSteps.length > 0;
  const showUserVariablesForm = userConfigurableVariables.length > 0;

  function closeTemplateApplyView(): void {
    onCloseStepInitializationView(getDefaultMainViewItemForAnalysis(analysis));
    TemplateApplyState.deleteState(analysis.id);
  }

  async function handleSubmit(): Promise<void> {
    logProfilerTimingEvent(APPLY_ANALYSIS_TEMPLATE_STARTED_EVENT);
    assert(templateApplyState.selectedTemplate != null, 'Expected selectedTemplate to be set');
    assert(
      templateApplyState.inputTables
        .filter((inputTable) => inputTable.inputType === InputTableType.VARIABLE)
        .every(
          (inputTable) =>
            inputTable.table != null &&
            inputTable.table.lineage[inputTable.table.lineage.length - 1].stepOutputReference != null
        ),
      'Expected a table to be set for all variables'
    );

    const templateVariableBindings: Array<TemplateVariableBinding> = [];
    if (hasOutputs) {
      assert(
        exportDatasetSteps.length === 0 || selectedFolderId != null,
        'Expected folder to be selected if export dataset steps exist'
      );
      assert(
        sendToDocumentSteps.length === 0 || selectedDocumentId != null,
        'Expected document to be selected if send to document steps exist'
      );

      const outputVariableContext: OutputVariableContext = {selectedFolderId, selectedDocumentId};
      templateVariableBindings.push(
        ...templateApplyState.selectedTemplate.configVariables
          .filter((configVariable) => {
            const config = OUTPUT_RELATED_VARIABLE_CONFIG[configVariable.configVariableKey];
            // Only include output-related variables that aren't excluded from submission
            return config != null && !config.excludeFromSubmission;
          })
          .map((configVariable) => {
            const config = OUTPUT_RELATED_VARIABLE_CONFIG[configVariable.configVariableKey];
            assert(config != null, 'Expected config to exist for output-related variable', {
              configKey: configVariable.configVariableKey,
            });
            return {
              variableId: configVariable.id,
              variableValue: config.getValue(outputVariableContext),
            };
          })
      );
    }

    if (showUserVariablesForm) {
      templateVariableBindings.push(
        ...userConfigurableVariables.map((configVariable) => ({
          variableId: configVariable.id,
          variableValue: userVariableBindings[configVariable.id] ?? null,
        }))
      );
    }

    await withLoadingScreen(
      AnalysisTemplateActions.applyAnalysisTemplate(analysis.id, {
        templateId: templateApplyState.selectedTemplate.id,
        templateVariableBindings,
        dataFrameVariableBindings: templateApplyState.inputTables
          .filter((table) => table.inputType === InputTableType.VARIABLE)
          .map((inputTable) => ({
            variableId: inputTable.variable!.id,
            newDataFrameId: getOutputDataForView(inputTable.table!, undefined, PipelineStepDataKind.DATASET)
              .id,
            newInputStepDataId:
              inputTable.table!.lineage[inputTable.table!.lineage.length - 1].stepOutputReference!
                .outputStepData.id,
            columnMapping: inputTable.columnMapping,
          })),
      })
    );
    closeTemplateApplyView();
    logProfilerTimingEvent(APPLY_ANALYSIS_TEMPLATE_FINISHED_EVENT);
    logProfilerTimingEvent(RUN_ANALYSIS_TEMPLATE_STARTED_EVENT);
  }

  function getInputTableDisabledMessage(
    analysis: AnalysisGraphQL,
    table: PipelineViewGraphQL,
    selectedTable: PipelineViewGraphQL | null
  ): string | null {
    const downstreamSteps = [
      ...getDownstreamSteps(
        analysis,
        table.lineage[table.lineage.length - 1].step.id,
        getOutputDataForView(table, analysis.steps, PipelineStepDataKind.DATASET).id
      ),
    ];
    if (downstreamSteps.some((step) => isAnalysisOutputStepType(step.definition.stepType))) {
      return 'Table cannot be selected because it is used to create outputs';
    }
    const isInputTableAlreadyUsed = templateApplyState.inputTables.some(
      (inputTable) =>
        inputTable.table != null &&
        getDefaultClientIdForView(inputTable.table, analysis.steps) ===
          getDefaultClientIdForView(table, analysis.steps)
    );
    const isInputTableSelected =
      selectedTable != null &&
      getDefaultClientIdForView(selectedTable, analysis.steps) ===
        getDefaultClientIdForView(table, analysis.steps);
    if (isInputTableAlreadyUsed && !isInputTableSelected) {
      return 'Table is already used as a variable starting table';
    }
    return null;
  }

  return (
    <div
      className={css(viewStyles.wrapper)}
      role="region"
      aria-label="Apply analysis template"
      {...getTestProps({component: 'AnalysisTemplateApplyView', element: 'root'})}
    >
      <div className={css(viewStyles.previewAreaPane)}>
        <ApplyAnalysisTemplatePreview templateAnalysis={templateApplyState.selectedTemplate} />
      </div>
      <div className={css(viewStyles.rightSidebar)}>
        <div>
          <FieldLabel label="Apply analysis template" />
          <AnalysisTemplateSelector
            templateApplyState={templateApplyState}
            onSelect={setSelectedTemplate}
            onSelectId={setSelectedTemplateId}
          />
        </div>
        {templateApplyState.inputTables.map((inputTable, index) => (
          <InputTableCard
            index={index}
            key={index}
            analysis={analysis}
            inputTable={inputTable}
            templateSteps={templateApplyState.selectedTemplate?.steps ?? []}
            onChange={({
              table,
              columnMapping,
            }: {
              table: PipelineViewGraphQL;
              columnMapping: {[templateColumn: string]: string};
            }) => {
              setTemplateApplyState({
                ...templateApplyState,
                inputTables: templateApplyState.inputTables.map((editingInputTable, i) =>
                  i === index ? {...editingInputTable, table, columnMapping} : editingInputTable
                ),
              });
            }}
            getInputTableDisabledMessage={getInputTableDisabledMessage}
          />
        ))}
        <div className={css(viewStyles.divider)} />
        {templateApplyState.selectedTemplate && hasOutputs && (
          <OutputsList
            template={templateApplyState.selectedTemplate}
            outputSteps={outputSteps}
            inputTables={templateApplyState.inputTables}
            analysisSteps={analysis.steps}
          />
        )}
        {showFolderSelector && (
          <>
            <span className={css(viewStyles.outputVariableText)}>Select a folder for saved datasets</span>
            <FolderSelector
              value={selectedFolderId}
              onChange={(e: {target: {value: string}}) => {
                setSelectedFolderId(e.target.value);
              }}
              // This prop allows us to select non-root folders
              showInventory={true}
            />
          </>
        )}
        {showDocumentInput && isSendToDocumentEnabled() && (
          <>
            <span className={css(viewStyles.outputVariableText)}>
              {getDocumentSelectorMessage()} <DefaultEntryOrWorksheetInfo analysis={analysis} />
            </span>
            <EntryAndWorksheetSearchInput
              autoFocus={false}
              isMulti={false}
              enableSearchEntries={isSendToEntryEnabled()}
              enableSearchWorksheets={isSendToWorksheetEnabled()}
              onRemove={() => {
                setSelectedDocumentId(null);
              }}
              onSelect={setSelectedDocumentId}
              dropLeft={true}
              selected={selectedDocumentId != null ? [selectedDocumentId] : []}
            />
          </>
        )}
        {showUserVariablesForm && templateApplyState.selectedTemplate && (
          <div className="u-small-spacing-top">
            <AnalysisTemplateVariablesForm
              clientStateId={`apply-template-${templateApplyState.selectedTemplate.id}`}
              variables={userConfigurableVariables}
              variableBindings={userVariableBindings}
              onVariableBindingsChange={setUserVariableBindings}
            />
          </div>
        )}
        <div className={css(viewStyles.footerButtons)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={closeTemplateApplyView}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          <Tooltip
            text="Cannot apply template because send to worksheet and send to entry are disabled"
            isDisabled={!hasDocumentOutputsButDisabled}
          >
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              disabled={
                templateApplyState.selectedTemplate == null ||
                templateApplyState.inputTables.filter(
                  (inputTable) => inputTable.inputType === InputTableType.VARIABLE && inputTable.table == null
                ).length !== 0 ||
                (showFolderSelector && selectedFolderId == null) ||
                (showDocumentInput && selectedDocumentId == null) ||
                hasDocumentOutputsButDisabled ||
                (showUserVariablesForm && !userVariableBindingsAreComplete)
              }
              onClick={handleSubmit}
              size={ButtonSize.MEDIUM}
              text="Apply"
              dataTestAttrs={{component: 'AnalysisTemplateApplyView', element: 'applyTemplateButton'}}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

const viewStyles = StyleSheet.create({
  previewAreaPane: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    overflowY: 'auto',
    height: '100%',
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: Colors.GRAY_LIGHTER,
    flexDirection: 'column',
    flexGrow: 1,
    overflowX: 'hidden',
  },
  wrapper: {
    backgroundColor: Colors.WHITE,
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
  rightSidebar: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 8,
    width: 223,
  },
  divider: {borderTop: `1px solid ${Colors.GRAY_LIGHT}`},
  footerButtons: {display: 'flex', justifyContent: 'flex-end', columnGap: '8px'},
  checkboxInput: {...FONT_PROXIMA_13},
  outputVariableText: {...FONT_PROXIMA_13, fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD},
});
